import React, { memo, useEffect, useState } from 'react';
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiPopover,
  EuiSelect,
  EuiText,
} from '@elastic/eui';
import axios from 'axios';
import { TranslateLanguage } from './KeywordTranslator';
import { useAlert } from './AlertProvider';

const getYouTubeId = (url = '') => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=|v\/|.+?v=))([^?&/\s]+)/i);
  return match ? match[1] : '';
};

const SearchPlaylistPopover = memo(({
  userId,
  videoURL,
  title,
  type = '詩歌',
  engineId,
  label = 'v',
  onLoginRequired,
}) => {
  const showAlert = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!isOpen || !userId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    axios.get('/api/playlists', { params: { type: 'song', userId } })
      .then(({ data }) => {
        if (cancelled) return;
        const nextPlaylists = Array.isArray(data) ? data : [];
        setPlaylists(nextPlaylists);
        setSelectedPlaylistId((current) => (
          nextPlaylists.some((playlist) => String(playlist.id) === String(current))
            ? current
            : (nextPlaylists[0]?.id ? String(nextPlaylists[0].id) : '')
        ));
      })
      .catch(() => {
        if (!cancelled) {
          setPlaylists([]);
          setSelectedPlaylistId('');
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, reloadToken]);

  const togglePopover = () => {
    if (!userId) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    setIsOpen((open) => !open);
  };

  const handleSave = async () => {
    const resourceId = getYouTubeId(videoURL);
    if (!selectedPlaylistId || !resourceId) {
      showAlert(TranslateLanguage(localStorage.userLanguage, '無法識別這支影片，請重新開啟後再試。'));
      return;
    }

    setIsSaving(true);
    try {
      const { data: existingSongs } = await axios.get(
        `/api/playlists/${selectedPlaylistId}/songs`,
        { params: { incrementPlayCount: false } },
      );

      const songAlreadyExists = Array.isArray(existingSongs)
        && existingSongs.some((song) => String(song.resourceId) === String(resourceId));

      if (songAlreadyExists) {
        showAlert(TranslateLanguage(localStorage.userLanguage, '詩歌已在歌單中'));
        setIsOpen(false);
        return;
      }

      await axios.post(`/api/playlists/${selectedPlaylistId}/addsongs`, {
        ResourceId: resourceId,
        Name: title,
        Link: videoURL,
        Type: type,
        Label: label,
        EngineID: engineId,
      });
      showAlert(TranslateLanguage(localStorage.userLanguage, '保存成功'));
      setIsOpen(false);
    } catch (error) {
      if (error?.response?.status === 409) {
        showAlert(TranslateLanguage(localStorage.userLanguage, '詩歌已在歌單中'));
      } else {
        showAlert(TranslateLanguage(localStorage.userLanguage, '保存失敗，請稍後再試'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const button = (
    <EuiButtonEmpty iconType="heart" size="xs" color="primary" onClick={togglePopover}>
      {TranslateLanguage(localStorage.userLanguage, '加入我的歌單')}
    </EuiButtonEmpty>
  );

  return (
    <EuiPopover
      button={button}
      isOpen={isOpen}
      closePopover={() => setIsOpen(false)}
      ownFocus
      panelPaddingSize="m"
      anchorPosition="upCenter"
    >
      <div style={{
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 96px)',
        overflowY: 'auto',
      }}>
        <EuiText size="s">
          <strong>{TranslateLanguage(localStorage.userLanguage, '選擇歌單')}</strong>
        </EuiText>

        <div style={{ marginTop: 12 }}>
          {isLoading && <EuiLoadingSpinner size="m" />}

          {!isLoading && loadError && (
            <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
              <EuiFlexItem>
                <EuiText size="s" color="danger">
                  {TranslateLanguage(localStorage.userLanguage, '歌單載入失敗，請重試')}
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton size="s" onClick={() => setReloadToken((value) => value + 1)}>
                  {TranslateLanguage(localStorage.userLanguage, '重試')}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          )}

          {!isLoading && !loadError && playlists.length === 0 && (
            <EuiText size="s" color="subdued">
              {TranslateLanguage(localStorage.userLanguage, '尚未建立歌單')}
            </EuiText>
          )}

          {!isLoading && !loadError && playlists.length > 0 && (
            <>
              <EuiSelect
                fullWidth
                value={selectedPlaylistId}
                onChange={(event) => setSelectedPlaylistId(event.target.value)}
                options={playlists.map((playlist) => ({
                  value: String(playlist.id),
                  text: playlist.name,
                }))}
              />
              <EuiFlexGroup
                justifyContent="flexEnd"
                gutterSize="s"
                responsive={false}
                style={{ marginTop: 12 }}
              >
                <EuiFlexItem grow={false}>
                  <EuiButtonEmpty onClick={() => setIsOpen(false)}>
                    {TranslateLanguage(localStorage.userLanguage, '關閉')}
                  </EuiButtonEmpty>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill isLoading={isSaving} onClick={handleSave}>
                    {TranslateLanguage(localStorage.userLanguage, '保存')}
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </>
          )}
        </div>
      </div>
    </EuiPopover>
  );
});

SearchPlaylistPopover.displayName = 'SearchPlaylistPopover';

export default SearchPlaylistPopover;
